// OtterBonk: the bits both ends share. Pure, no I/O: the Run DO (src/otterbonk/sim.ts) simulates with it, the client
// (otterbonk/client.js) predicts its own otter, places projectiles and decodes snapshots with it, Node tests run it.
//
// Sim 120 Hz, with network snapshots at 60 Hz. Timings are designed in 20 Hz ticks and converted with tk() (data.js), so
// changing TICK_HZ keeps the game's speed: px/s and per-second rates go through DT, durations through tk().
// Wire format (little-endian ArrayBuffer; rare messages are JSON text, see src/otterbonk/run.ts):
//   input   (client -> DO, 10 B): u8 MSG_INPUT, u16 seq, i8 dx, i8 dy, u8 flags (IN_DASH, IN_REVIVE held, IN_COIN), u32 the tick the
//                                page draws (0xffffffff: none)   one per client step (1/TICK_HZ)
//   ping     (client -> DO, 9 B): u8 MSG_PING, f64 clientTime                               echoed back as MSG_PONG (1 Hz)
//   snapshot (DO -> clients, one buffer for everyone):
//     header 35 B: u8 MSG_SNAP, u32 tick, u8 phase(bit 0x80 enraged), u8 level, u16 xpInto, u16 xpNeed, u16 kills,
//                  u8 nPlayers, u16 nEnemies, u16 nEvents, u8 bossFlags (1 present, 2 enraged, 4 hit flash),
//                  u16 bossHp, u16 bossMax, u16 bossX, u16 bossY, u8 bossState, u8 bossAngle, u16 nProj, u16 nGems,
//                  u8 bossKind (data.js BOSSES: a lord or the final boss), u8 nMarks
//     players 37 B each: u8 slot, u16 x, u16 y, u16 hp, u16 maxHp, u8 flags (PF_*), u16 ackSeq, u8 facing (dir8; the attack's
//                        while PF_ATK, else the last move's or, standing still, the last attack's),
//                        u8 speed px/s, u8 dashT, u8 dashCd, u8 revive (ticks), u8 class, u32 weapon levels (3 bits each,
//                        weapon 0 lowest), 2 x u32 passive levels (3 bits each, passive 0 lowest, 10 a word), u8 dash cooldown
//                        (ticks, from its stats; the client's prediction dashes with it), u8 复活币 held, u8 weapon levels
//                        from weapon 10 on (3 bits each, PROTO 10), u8 发展路线: route + 1 (0: none yet; data.js ROUTES)
//                        | its level << 5; bytes 34..36 hold the high bytes of dashCd, dashMax and revive.
//     enemies  8 B each: u16 id, u16 x, u16 y, u8 (kind & 7 | elite 8 | flash 16 | state << 5 (0..2) | trapped 128),
//                        u8 kind (PROTO 17: data.js ENEMIES index)
//     marks    5 B each: u8 kind (MK_*), u16 x, u16 y: what the minimap and the edge arrows show wherever it is (elites,
//                        chests), sent whole to every page (the enemies above are only the ones near its view)
//     events (variable): see EV_* sizes in EV_SIZE (props and drops, PROTO 9: EV_PROP / EV_PROP_GONE, EV_ITEM / EV_ITEM_GONE;
//                        an enemy's state 3 (ES_FROZEN) while a 冰冻海螺 holds it; PF_FAST while 加速海带 lasts;
//                        PROTO 12: EV_PROP's kind byte carries the hits it took, EV_PROP_HIT a hit on one)
//   Positions are quantised to 1/16 px (u16 covers 0..4095 px; the arena is 2560 px).
//   Projectiles and pearls are not sent per frame: only spawn/gone events. A projectile's path is a pure function of its
//   spawn event (projPos), so the client draws it without further traffic. Bubbles orbit by tick (bubblePos).

export const TICK_HZ = 120, SNAP_HZ = 60;
/** The original shipped balance is rounded on this clock, independent of the active simulation frequency. */
export const BALANCE_HZ = 30;
/** The wire's version: bump it whenever the binary layout or a message's meaning changes. The page connects with
 *  ?p=PROTO; the Run turns away any other (or none: pages from before it existed) with 「游戏更新了，刷新一下」 instead of
 *  feeding it snapshots it would misread (an old page decoding new players as 20 B drew nothing but its own otter). */
export const PROTO = 27; // 27: 120 Hz simulation / 60 Hz network snapshots plus fisher's net; 26: fisher's net, extra weapon and wider route field.
export const DT = 1 / TICK_HZ;
/** Preserve the shipped 30 Hz rounding, then express that same duration at the new tick rate. */
export const tk = (n20) => Math.max(1, Math.round(n20 * BALANCE_HZ / 20) * TICK_HZ / BALANCE_HZ);
/** Round a derived duration on the same shipped balance clock before expanding it to simulation ticks. */
export const roundTicks = (ticks) => Math.round(ticks * BALANCE_HZ / TICK_HZ) * TICK_HZ / BALANCE_HZ;
export const ARENA = 2560;            // px (160 x 160 tiles of 16 px): a run crosses it, regions apart (regionAt)
export const WALL = 24;               // deep water border nobody can enter
export const Q = 16;                  // position quantum: 1/16 px
export const PLAYER_R = 8;
export const MAX_PLAYERS = 4;
export const ENEMY_CAP = 420;
/** The run's timeline (a stage's): lords at LORD_AT (data.js MAPS[].lords), the final boss at BOSS_TICK, enraged at
 *  RUN_TICKS, gone OVERTIME_TICKS after that if it's still up. */
export const RUN_TICKS = 870 * TICK_HZ, BOSS_TICK = 780 * TICK_HZ, OVERTIME_TICKS = 60 * TICK_HZ;
export const LORD_AT = [240 * TICK_HZ, 510 * TICK_HZ];
export const DASH_STEPS = tk(5), DASH_CD = tk(80), DASH_MULT = 3;
export const STONE_SPEED = 300;       // px/s
/** Straight projectiles' speeds (px/s) by kind; the shell's path is its reach. */
export const PK_SPEED = [STONE_SPEED, 0, 420, 260, 150, 480, 320];

export const MSG_INPUT = 1, MSG_PING = 2, MSG_SNAP = 10, MSG_PONG = 11;
export const IN_DASH = 1;
/** Held: pulling up a teammate's ghost close by (拉人). Pressed (its first step): spend a 复活币. */
export const IN_REVIVE = 2, IN_COIN = 4;
export const PF_DOWN = 1, PF_INVULN = 2, PF_PICKING = 4, PF_DASH = 8, PF_OFFLINE = 16, PF_HURT = 32;
/** The otter is facing what it just attacked (snapshot facing = the attack's, for ATK_FACE ticks); when it stands still
 *  afterwards the facing stays on the last attack. */
export const PF_ATK = 64;
/** 加速海带: the otter is faster for a while (its speed is in the snapshot anyway; this is for the page's trail). */
export const PF_FAST = 128;
export const ATK_FACE = tk(6); // ~300 ms
export const PH_LOBBY = 0, PH_PLAY = 1, PH_OVER = 2;
export const ES_NORMAL = 0, ES_WINDUP = 1, ES_CHARGE = 2; // puffer inflating / shark aiming / an elite roaring; shark charging
/** On the wire only: the enemy is frozen (冰冻海螺), whatever it was doing. */
export const ES_FROZEN = 3;
export const BS_WALK = 0, BS_SLAM = 1, BS_INK = 2, BS_SUMMON = 3;
/** 鮟鱇大王 (the cave's boss): aiming its lunge (the line is telegraphed), lunging along it, calling the rockfall. */
export const BS_LUNGE = 4, BS_DASH = 5, BS_ROCKS = 6;
export const PK_STONE = 0, PK_SHELL = 1, PK_HOOK = 2, PK_PAN = 3, PK_BUB = 4, PK_BOLT = 5, PK_TSHOT = 6;
/** On EV_PROJ's kind: this spawn continues one that just ended (a bounce, a homing turn), not a new shot. */
export const PK_CONT = 0x80;
export const BA_SLAM = 0, BA_INK = 1, BA_SUMMON = 2;
/** The cave boss's attacks on EV_BOSS: a lunge (laid out as BA_SLAM: angle, delay, p1 length, p2 width), one falling
 *  rock (x, y where it lands, delay, p1 radius). */
export const BA_LUNGE = 3, BA_ROCKS = 4; // (BA_ROCKS p2: 0 a rock, 1 a 贝壳蟹's shell, 2 lightning (PROTO 18))
/** PROTO 16. Boss states: 瞬移 (it fades out where it is and comes up at a marked spot, a shockwave round it), 弹幕 (a
 *  wind-up, then a bullet pattern, standing), a phase change (it stands and roars). Their EV_BOSS: BA_BLINK (x, y where
 *  it comes up, delay, p1 the shockwave's radius), BA_SHOOT (x, y the boss, angle, delay the wind-up, p1 the pattern
 *  BP_*), BA_ROAR (x, y the boss, delay, p1 the phase it enters, 1..). A boss's moves (data.js BOSSES[].phases) are
 *  these states, plus the three bullet patterns as moves of their own (MV_*: each is BS_SHOOT on the wire). */
export const BS_BLINK = 7, BS_SHOOT = 8, BS_ROAR = 9;
export const BA_BLINK = 5, BA_SHOOT = 6, BA_ROAR = 7;
export const BP_RING = 0, BP_SPIRAL = 1, BP_AIMED = 2;
export const MV_RING = 16 + BP_RING, MV_SPIRAL = 16 + BP_SPIRAL, MV_AIMED = 16 + BP_AIMED;
/** A move selector only, not a wire state. Lightning uses the existing BA_ROCKS circle warning. */
export const MV_LIGHTNING = 19;
/** The boss flags' phase bits (header u8 at 18): (flags >> BF_PHASE) & 3, 0 the first phase. */
export const BF_PHASE = 3;
/** Enemy bullets (PROTO 16): a straight path from its spawn, a pure function of its age (bulletPos), so the page draws
 *  it from the spawn event alone. EV_BULLET: u16 id, u16 x, u16 y, u16 angle (65536 a turn), u8 speed / 4 (px/s), u16
 *  age at the snapshot, u16 life (ticks: it ends there, or where it would fly into a cave pillar), u8 kind (BULLET_R and
 *  the look). EV_BULLET_GONE: u16 id (it hit an otter). An otter is hit by one as its page draws them: the bullet as it
 *  was DRAW_LAG ticks ago (sim.ts) against the otter where it is. */
// EV_NET: u8 slot, u8 radius, u16 x/y, u8 duration. eventAge also applies when replaying a live net on reconnect.
export const EV_BULLET = 25, EV_BULLET_GONE = 26, EV_NET = 27;
export const BK_INK = 0, BK_SPINE = 1, BK_ORB = 2, BK_ZAP = 3;
export const BULLET_R = [5, 3, 7, 4];
export const a16 = (rad) => Math.round((rad / (Math.PI * 2)) * 65536) & 65535;
export const rad16 = (b) => (b / 65536) * Math.PI * 2;
/** Where a bullet is `age` ticks (may be fractional) after it left (x0, y0) at angle ang (rad), speed spd px/s. */
export function bulletPos(x0, y0, ang, spd, age, out = { x: 0, y: 0 }) { const d = spd * DT * age; out.x = x0 + Math.cos(ang) * d; out.y = y0 + Math.sin(ang) * d; return out; }
// (EV_BOOM: u16 x, u16 y, u8 radius, u8 whose (a slot; 255: an enemy's, a puffer bursting), PROTO 14)
export const EV_PROJ = 1, EV_PROJ_GONE = 2, EV_GEM = 3, EV_GEM_GONE = 4, EV_SPLASH = 5, EV_AIM = 6, EV_BOOM = 7, EV_BOSS = 8;
// class weapons: a turret put down (u8 owner, u16 id, u16 x, u16 y, u16 ticks left), a stone-hammer swing raised (u8 slot,
// u8 angle, u8 radius: it lands SWING_WIND ticks later, from wherever the otter is then: EV_BLOW says where, PROTO 13),
// the chef's heal (u8 slot, u8 radius)
export const EV_TURRET = 9, EV_SWING = 10, EV_HEAL = 11;
/** 大石锤: the wind-up, from the swing's EV_SWING to the blow (its hits, knockback and the page's impact frame). */
export const SWING_WIND = tk(5);
// 钓竿: a reel starts (u8 slot, u16 id of the enemy on the line (0 = the boss), u16 ticks at most): the line runs from the
// fisher to it until its EV_SLAP; the slam (u8 slot, u16 the same id, u16 x, u16 y, u8 radius (0: the line was dropped,
// no slam), u16 puddle ticks (0: none))
export const EV_REEL = 12, EV_SLAP = 13;
// 泡泡枪: a trapped enemy's bubble pops (u16 x, u16 y, u8 radius, u8 whose: a slot, 255 nobody's; PROTO 14)
export const EV_POP = 14;
// a passive / stat went off on an otter, for the page to show (u8 kind PR_*, u8 slot, u16 enemy id, u16 x, u16 y,
// u16 value): PR_DODGE it dodged a hit (闪避; x, y the otter); PR_STING its 水母伞 stung what hit it (id: the enemy, 0 the
// boss; x, y it; value: stun ticks); PR_CRIT a crit (x, y what it hit; value the damage); PR_LEECH 吸血 healed it lately
// (value HP)
// PR_PARRY 格挡 (刀客's route) blocked a hit (x, y the otter; id: what it was thrown back at, 0 the boss or nothing)
export const EV_PROC = 15, PR_DODGE = 0, PR_STING = 1, PR_CRIT = 2, PR_LEECH = 3, PR_PARRY = 4;
// props and drops: a breakable prop is there (u16 id, u16 x, u16 y, u8 kind: data.js PROPS) / broken or cleared away
// (u16 id, u8 how: 0 broken, 1 cleared); an item lies there (u16 id, u16 x, u16 y, u8 kind: data.js ITEMS, u16 ticks left)
// / gone (u16 id, u8 who took it: a slot, or ITEM_TIMEOUT when it sank away)
export const EV_PROP = 16, EV_PROP_GONE = 17, EV_ITEM = 18, EV_ITEM_GONE = 19, ITEM_TIMEOUT = 255;
// 唐刀 (刀客): a slash, already landed (u8 slot, u8 angle: where its arc is centred, u8 radius, u8 kind SL_*): SL_CW /
// SL_CCW a quick slash sweeping its arc (data.js WEAPONS[W_DAO].arc) clockwise / the other way on screen, SL_SPIN a
// 回旋斩 (the whole circle), SL_DASH a dash cut starting (angle: the dash's way; it cuts along the otter's path)
export const EV_SLASH = 20, SL_CW = 0, SL_CCW = 1, SL_SPIN = 2, SL_DASH = 3;
// (水花冲击 EV_SPLASH: u8 slot, u8 radius, u16 x, u16 y: the ring round where it went off, PROTO 13. 唐刀's EV_SLASH carries
// the u16 x, u16 y its arc is round too: drawn shape and hitbox are the same circle, wherever the otter has gone since.)
/** 唐刀: a slash sweeps its arc over SLASH_TICKS ticks (a 回旋斩 its circle over SPIN_TICKS), from its EV_SLASH's tick on:
 *  what it cuts is picked on its first tick, as one instant cut of its arc round where it started (x, y) would, and each
 *  is hit on the tick the blade is (k + 1) / n of the way round to it (sim.ts slashStart / slashStep), so an enemy is
 *  hit as the drawn blade passes it (client.js draws the same sweep, on the same ticks). */
export const SLASH_TICKS = tk(2), SPIN_TICKS = tk(3);
// a patch on the ground that hurts enemies for a while (u8 slot, u16 x, u16 y, u8 radius, u16 ticks, u8 kind ZN_*):
// ZN_SOUP 火锅's hot soup (厨师's route). (钓竿's puddle comes with its EV_SLAP.)
export const EV_ZONE = 21, ZN_SOUP = 1;
// a prop was hit and still stands (u16 id, u8 hits it has taken: data.js PROPS[].hp breaks it); EV_PROP's u8 is
// kind | hits << 4 (PROP_KIND: the kind's bits), so a full snapshot shows the cracks too
export const EV_PROP_HIT = 22, PROP_KIND = 15;
// a hit by an otter (sim.ts hit; for the page's damage numbers and hit feel, nothing else): u8 slot | DM_CRIT | DM_KILL,
// u16 the enemy's id (0: the boss), u16 the damage (rounded, at least 1). At most DMG_EV_MAX a slot a tick (a crowd
// cut at once: the rest go unsaid, their flash still shows), DMG_EV_ALL all otters together.
export const EV_DMG = 23, DM_SLOT = 7, DM_CRIT = 8, DM_KILL = 16, DMG_EV_MAX = 12, DMG_EV_ALL = 24;
// 大石锤 lands (u8 slot, u16 x, u16 y: the centre of the zone it hit, the page moves the drawn zone there)
export const EV_BLOW = 24;
// Every event ends in u8 eventAge, the number of simulation ticks since it occurred. Boss delays are stored from
// that original event tick; decoding subtracts eventAge, so a warning delivered after impact has a negative delay.
// Long-lived projectile/bullet ages and durations are u16 separately; other time fields use u16 where needed.
const EV_PAYLOAD_SIZE = { [EV_PROJ]: 15, [EV_PROJ_GONE]: 3, [EV_GEM]: 8, [EV_GEM_GONE]: 4, [EV_SPLASH]: 7, [EV_AIM]: 4, [EV_BOOM]: 7, [EV_BOSS]: 12, [EV_TURRET]: 10, [EV_SWING]: 4, [EV_HEAL]: 3, [EV_REEL]: 6, [EV_SLAP]: 11, [EV_POP]: 7, [EV_PROC]: 11, [EV_PROP]: 8, [EV_PROP_GONE]: 4, [EV_ITEM]: 10, [EV_ITEM_GONE]: 4, [EV_SLASH]: 9, [EV_ZONE]: 10, [EV_PROP_HIT]: 4, [EV_DMG]: 6, [EV_BLOW]: 6, [EV_BULLET]: 15, [EV_BULLET_GONE]: 3, [EV_NET]: 8 };
export const EV_SIZE = Object.fromEntries(Object.entries(EV_PAYLOAD_SIZE).map(([t, size]) => [t, size + 1]));
export const HEADER = 35, PLAYER_B = 37, ENEMY_B = 8, MARK_B = 5, N_WEAPONS = 12, N_PASSIVE_WIRE = 18;
/** Marks: an elite (where it waits), a chest (an elite's), a big chest (a lord's), an elite that roared and is coming
 *  for the team (PROTO 15). */
export const MK_ELITE = 1, MK_CHEST = 2, MK_BIG = 3, MK_HUNT = 4, MAX_MARKS = 24;

export const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
export const quant = (v) => clamp(Math.round(v * Q), 0, 65535);
export const a8 = (rad) => Math.round((rad / (Math.PI * 2)) * 256) & 255;
export const rad8 = (b) => (b / 256) * Math.PI * 2;

// ---- the arena: fixed rocks, the same on both ends (deterministic from a constant seed) ----
// Two maps (data.js MAPS): 0 海底 (sand, 88 loose rocks), 1 海底洞穴 (dark, ridges of rock pillars that also stop most
// shots, see solidAt). A run is the first (its 继续 overtime goes on to the next, in turn: data.js mapOf).
// Each map has five regions (regionAt): the middle where the run starts, and the four corners round it, each with its
// own enemies (data.js MAPS[].regions).
export const MAP_SEA = 0, MAP_CAVE = 1;
/** The middle region's half size (px): a square round the centre; outside it the quadrant: 1 NW, 2 NE, 3 SW, 4 SE. */
export const MID_R = 520;
export const regionAt = (x, y) => (Math.abs(x - ARENA / 2) < MID_R && Math.abs(y - ARENA / 2) < MID_R ? 0 : 1 + (x >= ARENA / 2 ? 1 : 0) + (y >= ARENA / 2 ? 2 : 0));
export const ROCK_R = [12, 18, 24]; // sprite sizes 32 / 48 / 64
function makeRocks() {
  let h = 20261029;
  const rnd = () => { h ^= h << 13; h ^= h >>> 17; h ^= h << 5; return (h >>> 0) / 4294967296; };
  const out = [];
  for (let tries = 0; out.length < 88 && tries < 8000; tries++) {
    const s = (rnd() * 3) | 0, r = ROCK_R[s], x = Math.round(80 + rnd() * (ARENA - 160)), y = Math.round(80 + rnd() * (ARENA - 160));
    if (Math.abs(x - ARENA / 2) < 130 && Math.abs(y - ARENA / 2) < 130) continue; // keep the start clear
    if (out.some((o) => (o.x - x) ** 2 + (o.y - y) ** 2 < (o.r + r + 48) ** 2)) continue;
    out.push({ x, y, r, s });
  }
  return out;
}
export const ROCKS = makeRocks();
/** The cave: ridges of overlapping pillars wandering across the arena (walls with corridors between them, at least
 *  CAVE_GAP px apart), the middle kept clear for the start. */
const CAVE_GAP = 40;
function makeCave() {
  let h = 20261030;
  const rnd = () => { h ^= h << 13; h ^= h >>> 17; h ^= h << 5; return (h >>> 0) / 4294967296; };
  const out = [];
  for (let ridge = 0, tries = 0; ridge < 64 && tries < 12000; tries++) {
    let x = 70 + rnd() * (ARENA - 140), y = 70 + rnd() * (ARENA - 140), a = rnd() * Math.PI * 2;
    const n = 4 + ((rnd() * 6) | 0), mine = [];
    for (let k = 0; k < n; k++) {
      const s = k === 0 || k === n - 1 ? (rnd() * 2) | 0 : 1 + ((rnd() * 2) | 0), r = ROCK_R[s];
      const ok = x > WALL + r + 30 && y > WALL + r + 30 && x < ARENA - WALL - r - 30 && y < ARENA - WALL - r - 30
        && Math.hypot(x - ARENA / 2, y - ARENA / 2) > 170 + r && !out.some((o) => (o.x - x) ** 2 + (o.y - y) ** 2 < (o.r + r + CAVE_GAP) ** 2);
      if (!ok) break;
      mine.push({ x: Math.round(x), y: Math.round(y), r, s });
      a += (rnd() - 0.5) * 1.1; const step = r * 1.15 + 4; x += Math.cos(a) * step; y += Math.sin(a) * step;
    }
    if (mine.length < 2) continue;
    out.push(...mine); ridge++;
  }
  return out;
}
export const CAVE_ROCKS = makeCave();
/** Each map's rocks, by map index. */
export const MAP_ROCKS = [ROCKS, CAVE_ROCKS];
/** A grid over each map's rocks (ROCK_CELL px cells, each listing, in order, the rocks within ROCK_REACH of it), so
 *  collide looks at the few rocks near a body instead of all of them (hundreds in the cave). */
const ROCK_CELL = 64, ROCK_REACH = 40, ROCK_W = Math.ceil(ARENA / ROCK_CELL);
for (const rocks of MAP_ROCKS) {
  const g = Array.from({ length: ROCK_W * ROCK_W }, () => []);
  rocks.forEach((k, i) => {
    const m = k.r + ROCK_REACH;
    for (let y = Math.max(0, Math.floor((k.y - m) / ROCK_CELL)); y <= Math.min(ROCK_W - 1, Math.floor((k.y + m) / ROCK_CELL)); y++)
      for (let x = Math.max(0, Math.floor((k.x - m) / ROCK_CELL)); x <= Math.min(ROCK_W - 1, Math.floor((k.x + m) / ROCK_CELL)); x++) g[y * ROCK_W + x].push(i);
  });
  rocks.grid = g;
}
/** Where shots stop (the cave only): a SOLID_CELL px grid, 1 where the cell's middle is inside a pillar. */
export const SOLID_CELL = 8;
const SOLID_W = Math.ceil(ARENA / SOLID_CELL);
const CAVE_SOLID = (() => {
  const g = new Uint8Array(SOLID_W * SOLID_W);
  for (const k of CAVE_ROCKS) for (let y = Math.floor((k.y - k.r) / SOLID_CELL); y <= Math.floor((k.y + k.r) / SOLID_CELL); y++)
    for (let x = Math.floor((k.x - k.r) / SOLID_CELL); x <= Math.floor((k.x + k.r) / SOLID_CELL); x++) {
      const cx = (x + 0.5) * SOLID_CELL, cy = (y + 0.5) * SOLID_CELL;
      if (x >= 0 && y >= 0 && x < SOLID_W && y < SOLID_W && (cx - k.x) ** 2 + (cy - k.y) ** 2 < (k.r - 2) ** 2) g[y * SOLID_W + x] = 1;
    }
  return g;
})();
/** Is (x, y) inside a pillar that stops shots on this map (only the cave's do)? */
export function solidAt(map, x, y) {
  if (map !== MAP_CAVE) return false;
  const cx = (x / SOLID_CELL) | 0, cy = (y / SOLID_CELL) | 0;
  return cx >= 0 && cy >= 0 && cx < SOLID_W && cy < SOLID_W && CAVE_SOLID[cy * SOLID_W + cx] === 1;
}
/** Nothing that stops shots on the straight line from (x0, y0) to (x1, y1) (sampled every SOLID_CELL px). */
export function clearLine(map, x0, y0, x1, y1) {
  if (map !== MAP_CAVE) return true;
  const n = Math.ceil(Math.hypot(x1 - x0, y1 - y0) / SOLID_CELL);
  for (let i = 1; i < n; i++) if (solidAt(map, x0 + ((x1 - x0) * i) / n, y0 + ((y1 - y0) * i) / n)) return false;
  return true;
}

/** Push a circle (o.x, o.y, radius rad) out of every rock and back inside the walls. Uses sqrt, not hypot: sqrt is
 *  exactly rounded everywhere, so the client's prediction lands on the same quantised spot as the server. */
export function collide(o, rad, rocks = ROCKS) {
  // the rocks near o (a map's: its grid, for a body up to ROCK_REACH px), else all of them; in their order either way
  const near = rocks.grid && rad <= ROCK_REACH ? rocks.grid[clamp((o.y / ROCK_CELL) | 0, 0, ROCK_W - 1) * ROCK_W + clamp((o.x / ROCK_CELL) | 0, 0, ROCK_W - 1)] : null;
  for (let n = near ? near.length : rocks.length, j = 0; j < n; j++) {
    const i = near ? near[j] : j;
    const k = rocks[i], R = k.r + rad, dx = o.x - k.x, dy = o.y - k.y, d2 = dx * dx + dy * dy;
    if (d2 >= R * R) continue;
    const d = Math.sqrt(d2);
    if (d < 1e-6) { o.x = k.x + R; continue; }
    o.x = k.x + (dx / d) * R; o.y = k.y + (dy / d) * R;
  }
  o.x = clamp(o.x, WALL + rad, ARENA - WALL - rad); o.y = clamp(o.y, WALL + rad, ARENA - WALL - rad);
}

/** Which way an otter faces for a move (dx, dy), 8 ways as the deck's dir8 (public/deck/move.js dir8Of, the same
 *  rule; drawn with looks.js facingFrame): 0 down, 1 down-right, 2 right, 3 up-right, 4 up, 5 up-left, 6 left,
 *  7 down-left. `prev` (the facing so far) is kept until the move is clearly past the halfway angle, so a move along a
 *  boundary doesn't flicker. The one place the facing is chosen (both ends; the snapshot's u8 facing carries it). */
export function facingOf(dx, dy, prev = -1) {
  const a = Math.atan2(dx, dy); // 0 = down, pi/2 = right
  const d = ((Math.round(a / (Math.PI / 4)) % 8) + 8) % 8;
  if (prev >= 0 && prev < 8 && prev !== d) {
    let off = Math.abs(a - prev * (Math.PI / 4)) % (2 * Math.PI); if (off > Math.PI) off = 2 * Math.PI - off;
    if (off < Math.PI / 8 + 0.14) return prev;
  }
  return d;
}
/** The 4-way facing (0 down, 1 right, 2 up, 3 left) nearest an 8-way one, for what is drawn 4 ways (the decorations);
 *  a diagonal goes with its up / down. */
export const facing4 = (d8) => [0, 0, 1, 2, 2, 2, 3, 0][d8 & 7];
/** For drawing only (the 3D otters, looks.js facing16): the 16-way facing of a move (0 down, then clockwise on
 *  screen: 4 right, 8 up, 12 left), kept within one 16th of the 8-way facing the game runs on (`d8`, the snapshot's),
 *  with the same hysteresis as facingOf. Never sent: the wire and the rules stay 8-way. */
export function facing16Of(dx, dy, prev, d8) {
  const near = (a) => { const d = Math.abs(a - d8 * 2) % 16; return Math.min(d, 16 - d) <= 1; };
  if (dx || dy) {
    const a = Math.atan2(dx, dy), S = Math.PI / 8; let d = ((Math.round(a / S) % 16) + 16) % 16;
    if (prev >= 0 && prev !== d) { let off = Math.abs(a - prev * S) % (2 * Math.PI); if (off > Math.PI) off = 2 * Math.PI - off; if (off < S / 2 + 0.07) d = prev; }
    return near(d) ? d : d8 * 2;
  }
  return prev >= 0 && near(prev) ? prev : d8 * 2;
}

/** One movement step (1/TICK_HZ) for an otter, the same on both ends. p: {x, y, spd, dashT, dashCd, down, map}
 *  (map: which map's rocks it bumps into, MAP_SEA if unset).
 *  dx/dy in -127..127: any angle. Its length is the speed share, capped at 1 (127): keys give 8 directions at full
 *  speed (a diagonal (127, 127) is no faster), a stick or a click target can give less. flags: IN_DASH. */
export function stepPlayer(p, dx, dy, flags = 0) {
  if (p.dashCd > 0) p.dashCd--;
  if ((flags & IN_DASH) && !p.down && p.dashCd === 0 && (dx || dy)) { p.dashT = DASH_STEPS; p.dashCd = p.dashMax || DASH_CD; }
  let sp = p.spd;
  if (p.dashT > 0) { p.dashT--; sp *= DASH_MULT; }
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len > 0) {
    const k = (Math.min(1, len / 127) * sp * DT) / len; // |move| <= 1 whatever the client sends
    p.x += dx * k; p.y += dy * k;
    collide(p, PLAYER_R, MAP_ROCKS[p.map | 0]);
    // Keep fractional movement between steps; quantize only on the wire. Rounding every 1/60 s biases speed.
  }
  return p;
}

/** Where a projectile is `age` ticks (may be fractional) after it spawned. Stones fly straight; a shell goes out
 *  `reach` px and comes back to where it was thrown over its life. */
export function projPos(kind, x0, y0, ang, age, life, reach, out = { x: 0, y: 0 }) {
  const d = kind === PK_SHELL ? reach * Math.sin((Math.PI * clamp(age, 0, life)) / life) : (PK_SPEED[kind] ?? STONE_SPEED) * DT * age;
  out.x = x0 + Math.cos(ang) * d; out.y = y0 + Math.sin(ang) * d;
  return out;
}
/** Bubble k of n around an otter at tick t (fractional on the client). */
export function bubbleAngle(t, k, n, spin, slot) { return (t * DT * spin) + (k * Math.PI * 2) / n + slot * 1.3; }

/** view: the tick the page is drawing the world at (its render tick, low 32 bits; -1: none): a page that draws, whose
 *  melee blows the sim judges against what it shows (sim.ts viewOf). */
export function encodeInput(seq, dx, dy, flags = 0, view = -1) {
  const b = new ArrayBuffer(10), v = new DataView(b);
  v.setUint8(0, MSG_INPUT); v.setUint16(1, seq & 0xffff, true); v.setInt8(3, dx); v.setInt8(4, dy); v.setUint8(5, flags);
  v.setUint32(6, view < 0 ? 0xffffffff : view >>> 0, true);
  return b;
}
export function decodeInput(buf) {
  const v = new DataView(buf), vw = buf.byteLength >= 10 ? v.getUint32(6, true) : 0xffffffff;
  return { seq: v.getUint16(1, true), dx: Math.max(-127, v.getInt8(3)), dy: Math.max(-127, v.getInt8(4)), flags: buf.byteLength > 5 ? v.getUint8(5) : 0, view: vw === 0xffffffff ? -1 : vw };
}
export function encodePing(t) {
  const b = new ArrayBuffer(9), v = new DataView(b);
  v.setUint8(0, MSG_PING); v.setFloat64(1, t, true);
  return b;
}

/** Decode a snapshot into plain objects (enemies as typed arrays). */
export function decodeSnapshot(buf) {
  const v = new DataView(buf);
  const ph = v.getUint8(5), bf = v.getUint8(18);
  const s = {
    tick: v.getUint32(1, true), phase: ph & 0x7f, level: v.getUint8(6), xpInto: v.getUint16(7, true), xpNeed: v.getUint16(9, true),
    kills: v.getUint16(11, true), nPlayers: v.getUint8(13), nEnemies: v.getUint16(14, true), nEvents: v.getUint16(16, true),
    boss: bf & 1 ? { enraged: !!(bf & 2), flash: !!(bf & 4), phase: (bf >> BF_PHASE) & 3, hp: v.getUint16(19, true), max: v.getUint16(21, true),
      x: v.getUint16(23, true) / Q, y: v.getUint16(25, true) / Q, state: v.getUint8(27), angle: rad8(v.getUint8(28)) } : null,
    nProj: v.getUint16(29, true), nGems: v.getUint16(31, true), bossKind: v.getUint8(33), nMarks: v.getUint8(34), marks: [],
    players: [], enemies: null, events: [],
  };
  let o = HEADER;
  for (let i = 0; i < s.nPlayers; i++, o += PLAYER_B) {
    const wl = v.getUint32(o + 18, true), w = [], pa = [];
    const wl2 = v.getUint8(o + 32);
    for (let k = 0; k < N_WEAPONS; k++) w.push(k < 10 ? (wl >>> (k * 3)) & 7 : (wl2 >>> ((k - 10) * 3)) & 7);
    for (let k = 0; k < N_PASSIVE_WIRE; k++) pa.push((v.getUint32(o + 22 + 4 * ((k / 10) | 0), true) >>> ((k % 10) * 3)) & 7);
    s.players.push({ slot: v.getUint8(o), x: v.getUint16(o + 1, true) / Q, y: v.getUint16(o + 3, true) / Q, hp: v.getUint16(o + 5, true),
      maxHp: v.getUint16(o + 7, true), flags: v.getUint8(o + 9), ack: v.getUint16(o + 10, true), facing: v.getUint8(o + 12),
      spd: v.getUint8(o + 13), dashT: v.getUint8(o + 14), dashCd: v.getUint8(o + 15) + v.getUint8(o + 34) * 256, revive: v.getUint8(o + 16) + v.getUint8(o + 36) * 256, cls: v.getUint8(o + 17),
      w, pa, dashMax: v.getUint8(o + 30) + v.getUint8(o + 35) * 256, coins: v.getUint8(o + 31), route: (v.getUint8(o + 33) & 31) - 1, rlv: v.getUint8(o + 33) >> 5 });
  }
  // kind: the flags byte (its low 3 bits are the kind's), type: the kind
  const n = s.nEnemies, id = new Uint16Array(n), x = new Float32Array(n), y = new Float32Array(n), kind = new Uint8Array(n), type = new Uint8Array(n);
  for (let i = 0; i < n; i++, o += ENEMY_B) {
    id[i] = v.getUint16(o, true); x[i] = v.getUint16(o + 2, true) / Q; y[i] = v.getUint16(o + 4, true) / Q; kind[i] = v.getUint8(o + 6); type[i] = v.getUint8(o + 7);
  }
  s.enemies = { id, x, y, kind, type };
  for (let i = 0; i < s.nMarks; i++, o += MARK_B) s.marks.push({ kind: v.getUint8(o), x: v.getUint16(o + 1, true) / Q, y: v.getUint16(o + 3, true) / Q });
  for (let i = 0; i < s.nEvents; i++) {
    const t = v.getUint8(o), u16 = (k) => v.getUint16(o + k, true), u8 = (k) => v.getUint8(o + k);
    const size = EV_SIZE[t]; if (!size || o + size > buf.byteLength) break;
    if (t === EV_PROJ) s.events.push({ t, id: u16(1), x: u16(3) / Q, y: u16(5) / Q, angle: rad8(u8(7)), life: u16(8), age: u16(10), kind: u8(12) & 0x7f, cont: !!(u8(12) & PK_CONT), reach: u8(13), owner: u8(14) });
    else if (t === EV_TURRET) s.events.push({ t, owner: u8(1), id: u16(2), x: u16(4) / Q, y: u16(6) / Q, left: u16(8) });
    else if (t === EV_SWING) s.events.push({ t, slot: u8(1), angle: rad8(u8(2)), r: u8(3) });
    else if (t === EV_BLOW) s.events.push({ t, slot: u8(1), x: u16(2) / Q, y: u16(4) / Q });
    else if (t === EV_HEAL) s.events.push({ t, slot: u8(1), r: u8(2) });
    else if (t === EV_SLASH) s.events.push({ t, slot: u8(1), angle: rad8(u8(2)), r: u8(3), kind: u8(4), x: u16(5) / Q, y: u16(7) / Q });
    else if (t === EV_REEL) s.events.push({ t, slot: u8(1), id: u16(2), ticks: u16(4) });
    else if (t === EV_SLAP) s.events.push({ t, slot: u8(1), id: u16(2), x: u16(4) / Q, y: u16(6) / Q, r: u8(8), zone: u16(9) });
    else if (t === EV_POP) s.events.push({ t, x: u16(1) / Q, y: u16(3) / Q, r: u8(5), slot: u8(6) === 255 ? -1 : u8(6) });
    else if (t === EV_PROC) s.events.push({ t, kind: u8(1), slot: u8(2), id: u16(3), x: u16(5) / Q, y: u16(7) / Q, val: u16(9) });
    else if (t === EV_PROJ_GONE) s.events.push({ t, id: u16(1) });
    else if (t === EV_PROP) s.events.push({ t, id: u16(1), x: u16(3) / Q, y: u16(5) / Q, kind: u8(7) & PROP_KIND, hits: u8(7) >> 4 });
    else if (t === EV_PROP_HIT) s.events.push({ t, id: u16(1), hits: u8(3) });
    else if (t === EV_PROP_GONE) s.events.push({ t, id: u16(1), how: u8(3) });
    else if (t === EV_ITEM) s.events.push({ t, id: u16(1), x: u16(3) / Q, y: u16(5) / Q, kind: u8(7), left: u16(8) });
    else if (t === EV_ITEM_GONE) s.events.push({ t, id: u16(1), by: u8(3) });
    else if (t === EV_GEM) s.events.push({ t, id: u16(1), x: u16(3) / Q, y: u16(5) / Q, tier: u8(7) });
    else if (t === EV_GEM_GONE) s.events.push({ t, id: u16(1), by: u8(3) });
    else if (t === EV_SPLASH) s.events.push({ t, slot: u8(1), r: u8(2), x: u16(3) / Q, y: u16(5) / Q });
    else if (t === EV_NET) s.events.push({ t, slot: u8(1), r: u8(2), x: u16(3) / Q, y: u16(5) / Q, ticks: u8(7) });
    else if (t === EV_AIM) s.events.push({ t, id: u16(1), angle: rad8(u8(3)) });
    else if (t === EV_BOOM) s.events.push({ t, x: u16(1) / Q, y: u16(3) / Q, r: u8(5), slot: u8(6) === 255 ? -1 : u8(6) });
    else if (t === EV_ZONE) s.events.push({ t, slot: u8(1), x: u16(2) / Q, y: u16(4) / Q, r: u8(6), ticks: u16(7), kind: u8(9) });
    else if (t === EV_DMG) { const f = u8(1); s.events.push({ t, slot: f & DM_SLOT, crit: !!(f & DM_CRIT), kill: !!(f & DM_KILL), id: u16(2), dmg: u16(4) }); }
    else if (t === EV_BULLET) s.events.push({ t, id: u16(1), x: u16(3) / Q, y: u16(5) / Q, angle: rad16(u16(7)), speed: u8(9) * 4, age: u16(10), life: u16(12), kind: u8(14) });
    else if (t === EV_BULLET_GONE) s.events.push({ t, id: u16(1) });
    else if (t === EV_BOSS) s.events.push({ t, kind: u8(1), x: u16(2) / Q, y: u16(4) / Q, angle: rad8(u8(6)), delay: u16(7) - u8(size - 1), p1: u8(9), p2: u16(10) });
    else break;
    s.events[s.events.length - 1].eventAge = u8(size - 1);
    o += size;
  }
  return s;
}
